import clsx from 'clsx';
import React from 'react';

import Byline from '@components/Byline';
import ArticleHeader from '@components/FeedItems/ArticleHeader';
import ArticleImage from '@components/FeedItems/ArticleImage';
import Highlight from '@components/FeedItems/Highlight';
import TimeAgo from '@components/TimeAgo';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click, transformUnitNameToLocation } from '@tracking';

import { Article, FeedUnit } from '../../../interfaces/homepage';
import { SecondaryButton } from '../../Button/variants';
import ArticleFeedItem from '../../FeedItems/ArticleFeedItem';
import UnitHeader from '../../FeedItems/UnitHeader';

interface NewsletterUnitProps {
  feedUnit: FeedUnit;
  isLatestArticle?: boolean;
  isTheWeekend?: boolean;
}

const NewsletterUnit = ({
  feedUnit: { unitName, unitDescription, viewAllUrl, content },
  isLatestArticle = false,
  isTheWeekend = false,
}: NewsletterUnitProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: transformUnitNameToLocation(unitName),
  });

  return (
    <Track>
      <div className="border-0 border-b border-solid border-grayscale-gray px-16 py-32 sm:px-0 sm:py-48">
        <UnitHeader
          isTheWeekend={isTheWeekend}
          unitName={unitName}
          unitDescription={unitDescription}
          viewAllUrl={viewAllUrl}
        />
        <div className="flex flex-col justify-between sm:flex-row">
          {(content as Article[]).map(
            (
              { authors, highlight, id, isPro, newsletter, picture, publishedAt, slug, title },
              index
            ) => (
              <ArticleFeedItem
                articleId={id}
                articleAuthors={authors}
                contentIndex={index}
                key={`${unitName}-${id}`}
                className="flex flex-col pb-36 sm:w-min sm:pb-0"
              >
                <ArticleImage
                  className={clsx({
                    'sm:h-[calc(330px*9/16)] sm:w-[330px] lg:h-[calc(416px*9/16)] lg:w-[416px]':
                      !isLatestArticle,
                    'sm:h-[calc(213px*9/16)] sm:w-[213px] lg:h-[calc(257px*9/16)] lg:w-[257px]':
                      isLatestArticle,
                  })}
                  id={id}
                  isPro={isPro}
                  picture={picture}
                  sizes="(min-width: 1200px) 500px, (min-width: 768px) 300px, (min-width: 480px) 600px, 300px"
                  slug={slug}
                  title={title}
                />
                <div className="mt-8 sm:mt-12">
                  {(highlight || (newsletter && isLatestArticle)) && (
                    <Highlight highlight={isLatestArticle ? newsletter : highlight} />
                  )}
                  <ArticleHeader
                    className={clsx('text-headline-xs', {
                      'latest-article-title sm:text-headline-xxs': isLatestArticle,
                      'sm:text-headline-sm': !isLatestArticle,
                    })}
                    title={title}
                    slug={slug}
                  />
                  {isLatestArticle ? (
                    <TimeAgo className="block text-grayscale-gray" date={publishedAt} />
                  ) : (
                    <Byline authors={authors} publishedAt={publishedAt} />
                  )}
                </div>
              </ArticleFeedItem>
            )
          )}
        </div>
        <SecondaryButton
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ViewAllArticles }))}
          as="link"
          href={viewAllUrl}
          className="inline-block sm:hidden"
        >
          View all
        </SecondaryButton>
      </div>
    </Track>
  );
};

export default NewsletterUnit;
